/*
 * Copyright (c) CIRROM 2022.
 */

import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import {
  acceptSuggestedChangesWithModifications,
  approveAdHocSuggestedChange,
  approveMultipleChange,
  approveSuggestedChange,
  getCRMContactForSuggestedChange,
  rollbackSuggestedChange,
  transitionSuggestedChange,
} from "../../api/suggestedchanges";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import Tooltip from "@mui/material/Tooltip";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import { ProbabilityCellRenderer, ValueChangeRenderer } from "./CellRenderers";
import Box from "@mui/material/Box";
import Rating from "@mui/material/Rating";
import Divider from "@mui/material/Divider";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SpellcheckOutlinedIcon from "@mui/icons-material/SpellcheckOutlined";
import DomainIcon from "@mui/icons-material/Domain";
import MenuItem from "@mui/material/MenuItem";
import WarningIcon from "@mui/icons-material/Warning";
import LinkIcon from "@mui/icons-material/Link";
import classes from "./css/SuggestedChangesRow.module.css";
import { isEmpty } from "lodash";
import { AccountNameAutocomplete } from "../utils/datasources/AutoComplete";

interface CirromChanges {
  cirrom_value: string;
  probability: number;
  suggested_change_id: number;
  status: string;
  description: any;
  link_change_type: any;
}

function ApproveChangesButton(
  props: Readonly<{
    disabled: boolean;
    onClick: () => void;
  }>
) {
  return (
    <Tooltip title={"Push the manual overwrite to your CRM."}>
      <IconButton
        color={"primary"}
        disabled={props.disabled}
        onClick={props.onClick}
      >
        <SpellcheckOutlinedIcon />
      </IconButton>
    </Tooltip>
  );
}
function BackButton(props: Readonly<{ onClick: () => void }>) {
  return (
    <Tooltip title={"Go back to the previous value."}>
      <IconButton onClick={props.onClick}>
        <ArrowBackIcon />
      </IconButton>
    </Tooltip>
  );
}

export const EditableValueRenderer = (props: {
  children: any;
  value: string;
  suggestedExplainEntry: any;
  onApprove(localValue: string): void;
  accountAutocompleteSystemId?: number;
  onApproveAccount?(account: { name: string; crm_account_id: string }): void;
}) => {
  const [localChangeEnabled, setLocalChangeEnabled] = useState<boolean>(false);
  const [localValue, setLocalValue] = useState<string>(props.value);
  const [selectedAccount, setSelectedAccount] = useState<{
    name: string;
    crm_account_id: string;
  }>();
  const useAccountAutocomplete = props.accountAutocompleteSystemId != null;
  return (
    <>
      {localChangeEnabled ? (
        <div className={classes.adHocChangeEnabledBox}>
          {useAccountAutocomplete ? (
            <AccountNameAutocomplete
              integratedSystemId={props.accountAutocompleteSystemId}
              onSelectionChanged={setSelectedAccount}
            />
          ) : (
            <TextField
              fullWidth
              variant="standard"
              size="small"
              margin="dense"
              value={localValue}
              onChange={(event) => setLocalValue(event.target.value)}
              multiline
            />
          )}
          <BackButton
            onClick={() => {
              setLocalValue(props.value);
              setSelectedAccount(undefined);
              setLocalChangeEnabled(false);
            }}
          />
          <ApproveChangesButton
            disabled={
              useAccountAutocomplete
                ? !selectedAccount
                : localValue === props.suggestedExplainEntry.crm_value
            }
            onClick={() => {
              if (useAccountAutocomplete) {
                props.onApproveAccount?.(selectedAccount);
              } else {
                props.onApprove(localValue);
              }
              setLocalChangeEnabled(false);
              setLocalValue(props.value);
              setSelectedAccount(undefined);
            }}
          />
        </div>
      ) : (
        <div className={classes.adHocChangeBox}>
          {props.children}
          <div>
            <Tooltip
              title={"Edit this value through Cirrom."}
              placement={"left"}
            >
              <IconButton
                onClick={() => {
                  setLocalChangeEnabled(true);
                }}
                style={{ height: "20px" }}
              >
                <DriveFileRenameOutlineOutlinedIcon fontSize={"small"} />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Represents a Reject Button component.
 *
 * @typedef {Object} RejectButton
 * @property {function} onActionInitiated - Function to call when the action is initiated.
 * @property {number} changeId - The ID of the change.
 * @property {function} onSuccess - Function to call when the action is successful.
 */
const RejectButton = (props: {
  onActionInitiated(): void;
  onActionFailed(message: string): void;
  changeId: number;
  onSuccess(changeId: number): void;
}) => {
  return (
    <Tooltip
      title={"Reject this change so Cirrom won't suggest it in the future."}
      placement={"left"}
    >
      <IconButton
        size={"small"}
        data-testid={"reject-button"}
        onClick={() => {
          props.onActionInitiated();
          transitionSuggestedChange([props.changeId], true)
            .then((_response) => props.onSuccess(props.changeId))
            .catch((err) => {
              console.error(err);
              props.onActionFailed(
                "That change could not be rejected. It is still pending."
              );
            });
        }}
      >
        <CancelOutlinedIcon />
      </IconButton>
    </Tooltip>
  );
};

/**
 * Represents a button component for approving a suggested change.
 *
 * @param {Object} props - The properties for the component.
 * @param {Function} props.onActionInitiated - The function to be called when the action is initiated.
 * @param {number} props.changeId - The ID of the change.
 * @param {Function} props.onSuccess - The function to be called when the change is successfully approved.
 * @param {Function} props.displayAlert - The function to display an alert message.
 * @returns {JSX.Element} The JSX element representing the approve now button.
 */
const ApproveNowButton = (props: {
  onActionInitiated(): void;
  onActionFailed(message: string): void;
  changeId: number;
  onSuccess(changeId: number): void;
  displayAlert(texts: string, status: string): void;
  multipleChoiceId?: string;
}) => {
  const handleApprovalResponse = (response) => {
    if (!isEmpty(response?.failures)) {
      const first_key = Object.keys(response["failures"])[0];
      props.displayAlert(
        `Unable to change value\n ${response["failures"][first_key]}`,
        "error"
      );
    } else {
      props.displayAlert("Change successfully accepted.", "success");
    }

    props.onSuccess(props.changeId);
  };

  return (
    <Tooltip title={"Write this change to your CRM"} placement={"right"}>
      <IconButton
        size={"small"}
        color="primary"
        onClick={() => {
          props.onActionInitiated();
          if (props.multipleChoiceId) {
            approveMultipleChange(props.changeId, props.multipleChoiceId)
              .then((response) => {
                handleApprovalResponse(response);
              })
              .catch((err) => {
                console.error(err);
                props.onActionFailed(
                  "That change did not reach your CRM. It is still pending."
                );
              });
          } else {
            approveSuggestedChange(props.changeId)
              .then((response) => {
                handleApprovalResponse(response);
              })
              .catch((err) => {
                console.error(err);
                props.onActionFailed(
                  "That change did not reach your CRM. It is still pending."
                );
              });
          }
        }}
      >
        <CheckCircleOutlinedIcon />
      </IconButton>
    </Tooltip>
  );
};

/**
 * Renders a change action for a suggested explain entry.
 *
 * @param {Object} props - The props object containing the necessary data for rendering the change action.
 * @param {any} props.suggestedExplainEntry - The suggested explain entry object.
 * @param {number} props.changeIndex - The index of the change in the suggested explain entry.
 * @param {number} props.editableChangeIndex - The index of the editable change.
 * @param {string} props.fieldName - The name of the field for the change action.
 * @param {function} props.onActionInitiated - The callback function to be called when an action is initiated.
 * @param {function} props.displayAlert - The function for displaying an alert.
 * @param {function} props.onSuccess - The callback function to be called on success.
 *
 * @returns {React.Element} - The rendered change action component.
 */
const ChangeActionRenderer = (props: {
  suggestedExplainEntry: any;
  changeIndex: number;
  editableChangeIndex: number;
  fieldName: string;
  onActionInitiated(): void;
  onActionFailed(message: string): void;
  displayAlert(text: string, status: string): void;
  onSuccess(changeId: number): void;
}) => {
  const replaySuggestedChange = async () => {
    const change =
      props.suggestedExplainEntry.cirrom_changes[props.changeIndex];
    const crmData = await getCRMContactForSuggestedChange(
      change.suggested_change_id
    );
    if (
      crmData &&
      crmData[props.suggestedExplainEntry.field_name] === change.cirrom_value
    ) {
      props.onActionInitiated();
      rollbackSuggestedChange(change.suggested_change_id)
        .then((_response) => {
          props.displayAlert("Value successfully rolled back.", "success");
          props.onSuccess(change.suggested_change_id);
        })
        .catch((err) => {
          console.error(err);
          props.onActionFailed(
            "That value could not be rolled back. Nothing has changed."
          );
        });
    } else {
      props.displayAlert(
        "Field value has changed since CIRROM read it.",
        "warning"
      );
    }
  };
  return (
    <>
      {props.suggestedExplainEntry?.cirrom_changes?.[props.changeIndex]
        .status === "Rejected" && (
        <Typography
          variant={"h6"}
          sx={{ color: "error.main", fontWeight: "bold" }}
        >
          Rejected
        </Typography>
      )}
      {props.suggestedExplainEntry?.cirrom_changes?.[props.changeIndex]
        .status === "Approved" && (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant={"h6"}
            sx={{ color: "success.main", fontWeight: "bold" }}
          >
            Approved
          </Typography>

          {["Address Linkage", "Company"].indexOf(props.fieldName) < 0 && (
            <IconButton
              style={{ margin: "2px", padding: "0" }}
              data-testid={"replay-button"}
              onClick={() => {
                replaySuggestedChange().catch((err) => {
                  console.error(err);
                  props.onActionFailed(
                    "That value could not be rolled back. Nothing has changed."
                  );
                });
              }}
            >
              <ReplayOutlinedIcon />
            </IconButton>
          )}
        </div>
      )}

      {(props.suggestedExplainEntry.cirrom_changes[props.changeIndex].status ===
        "Active" ||
        (props.suggestedExplainEntry.cirrom_changes[props.changeIndex]
          .probability === null &&
          props.suggestedExplainEntry.cirrom_changes[props.changeIndex]
            .status === "Expired")) && (
        <div
          style={{
            display: "flex",
            height: "24px",
          }}
        >
          {props.editableChangeIndex !== props.changeIndex && (
            <RejectButton
              onActionInitiated={props.onActionInitiated}
              onActionFailed={props.onActionFailed}
              onSuccess={props.onSuccess}
              changeId={
                props.suggestedExplainEntry.cirrom_changes[props.changeIndex]
                  .suggested_change_id
              }
            />
          )}

          {props.editableChangeIndex !== props.changeIndex &&
            props.suggestedExplainEntry.cirrom_changes[props.changeIndex]
              .cirrom_value && (
              <ApproveNowButton
                onActionInitiated={props.onActionInitiated}
                onActionFailed={props.onActionFailed}
                changeId={
                  props.suggestedExplainEntry.cirrom_changes[props.changeIndex]
                    .suggested_change_id
                }
                onSuccess={props.onSuccess}
                displayAlert={props.displayAlert}
              />
            )}
        </div>
      )}
    </>
  );
};

/**
 * Renders a multiple choice change component.
 *
 * @param {Object} props - The props object.
 * @param {CirromChanges} props.change - The change object.
 * @param {function} props.onActionInitiated - The function to be called when an action is initiated.
 * @param {function} props.onSuccess - The function to be called when the change is successful.
 * @returns {JSX.Element} - The rendered multiple choice change component.
 */
const MultipleChoiceChangeRenderer = (props: {
  change: CirromChanges;
  onActionInitiated(): void;
  onActionFailed(message: string): void;
  onSuccess(changeId: number): void;
  displayAlert(text: string, status: string): void;
}) => {
  const [selectedValue, setSelectedValue] = useState("");

  const getAccountLabel = (entry) => {
    const withContactType =
      entry?.company_type && `${entry.new_value}(${entry.company_type})`;
    if (!isEmpty(withContactType)) {
      return withContactType;
    }
    return entry.new_value;
  };
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <TextField
        select
        size={"small"}
        variant={"standard"}
        placeholder={"Multiple Matches Available"}
        value={selectedValue}
        onChange={(event) => setSelectedValue(event.target.value)}
        style={{ minWidth: "200px", marginBottom: "5px" }}
      >
        {JSON.parse(props.change.cirrom_value).map((entry) => (
          <MenuItem key={`${entry.new_value}-${entry.id}`} value={entry.id}>
            {getAccountLabel(entry)}
          </MenuItem>
        ))}
      </TextField>
      <div>
        <RejectButton
          onActionInitiated={props.onActionInitiated}
          onActionFailed={props.onActionFailed}
          changeId={props.change.suggested_change_id}
          onSuccess={props.onSuccess}
        />
        <ApproveNowButton
          onActionInitiated={props.onActionInitiated}
          onActionFailed={props.onActionFailed}
          changeId={props.change.suggested_change_id}
          onSuccess={props.onSuccess}
          displayAlert={props.displayAlert}
          multipleChoiceId={selectedValue}
        />
      </div>
    </div>
  );
};

/**
 * Renders a table row for a suggested change.
 *
 * @param {Object} props - The props object.
 * @param {RowNode | any} props.selectedNode - The selected node.
 * @param {string} props.fieldName - The name of the field.
 * @param {boolean} props.fieldFromAccount - Indicates if the field is linked from the account.
 * @param {function} props.onSuccess - The success callback function.
 * @param {function} props.displayAlert - The display alert function.
 * @param {function} props.onActionInitiated - The action initiated function.
 * @param {Object} props.suggestedExplainEntry - The suggested explain entry object.
 * @param {string} props.suggestedExplainEntry.crm_value - The CRM value.
 * @param {Array<Object>} props.suggestedExplainEntry.cirrom_changes - The array of cirrom changes.
 * @param {function} props.suggestedExplainEntry.field_name - The field name.
 *
 * @returns {JSX.Element} - The rendered table row.
 */
export function TableRowForSuggestedChange(
  props: Readonly<{
    suggestedChangeId: number;
    fieldName: string;
    fieldFromAccount: boolean;
    integratedSystemId?: number;
    onSuccess(suggestedId?): void;
    displayAlert(text: string, variant: string): void;
    onActionInitiated(): void;
    onActionFailed(message: string): void;
    suggestedExplainEntry: {
      isTextCompany?: boolean;
      crm_value: string;
      cirrom_changes: CirromChanges[];
      field_name: string;
      crm_id: string;
      textCompany?: any;
      alt_text?: string;
    };
  }>
) {
  const [editableChangeIndex, setEditableChangeIndex] =
    useState<number>(undefined);

  /*
   * A cell Cirrom proposes to change carries the attention colour; an unchanged one
   * carries none at all. Palette path rather than a literal, so it resolves through
   * the theme and stays legible in the dark variant - which means it must go to sx.
   */
  const getCellSx = () =>
    suggestedExplainEntry?.cirrom_changes?.length > 0
      ? { color: "error.main", fontWeight: "bold" }
      : {};

  const {
    fieldName,
    fieldFromAccount,
    suggestedExplainEntry,
    onSuccess,
    displayAlert,
  } = props;
  return (
    <TableRow>
      <TableCell className={classes.tableCell}>
        <div className={classes.tableCellHeaderContents}>
          <Tooltip title={suggestedExplainEntry?.alt_text}>
            <Typography variant={"h5"} className={classes.tableCellHeaderText}>
              {fieldName}
            </Typography>
          </Tooltip>
          {fieldFromAccount && (
            <Tooltip title={"Field Linked From Account"}>
              <DomainIcon className={classes.fieldLinkedIcon} />
            </Tooltip>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className={classes.tableCellCRMContents}>
          {fieldName === "Company" && suggestedExplainEntry?.isTextCompany ? (
            <div className={classes.textCompanyValue}>
              <Tooltip title={"Company name stored as text in InterAction."}>
                <WarningIcon
                  className={classes.textCompanyIcon}
                  sx={{ color: "warning.main" }}
                />
              </Tooltip>

              {suggestedExplainEntry?.textCompany.crm_value}
            </div>
          ) : (
            suggestedExplainEntry?.crm_value || ""
          )}
        </div>
      </TableCell>
      <TableCell sx={getCellSx()}>
        <div className={classes.tableCellSuggestedContents}>
          {suggestedExplainEntry?.cirrom_changes?.map((change, changeIndex) => {
            const descriptionText =
              change.description?.Description ||
              (typeof change.description === "string"
                ? JSON.parse(change.description).Description
                : undefined);
            const isCompanyNotPopulated =
              fieldName === "Company" &&
              !change.cirrom_value &&
              typeof descriptionText === "string" &&
              descriptionText
                .toLowerCase()
                .includes("company field is not populated");
            return (
              <div
                className={
                  change?.link_change_type
                    ? classes.linkedChangeWrapper
                    : classes.changeBox
                }
                key={`${change.suggested_change_id}-${change.cirrom_value}-${change.link_change_type}`}
              >
                {change?.link_change_type && (
                  <LinkIcon
                    fontSize={"small"}
                    className={classes.linkedChangeIcon}
                    sx={{ color: "success.main" }}
                  />
                )}
                <Box
                  key={changeIndex}
                  className={
                    change?.link_change_type
                      ? classes.linkedChangeBox
                      : undefined
                  }
                  sx={
                    change?.link_change_type
                      ? { borderColor: "success.main" }
                      : undefined
                  }
                >
                  <EditableValueRenderer
                    value={change.cirrom_value}
                    suggestedExplainEntry={suggestedExplainEntry}
                    accountAutocompleteSystemId={
                      isCompanyNotPopulated
                        ? props.integratedSystemId
                        : undefined
                    }
                    onApproveAccount={(account) => {
                      props.onActionInitiated();
                      acceptSuggestedChangesWithModifications(
                        change.suggested_change_id,
                        account.name,
                        account.crm_account_id
                      )
                        .then((_onAcceptResponse) => {
                          displayAlert(
                            "Change successfully accepted.",
                            "success"
                          );
                          onSuccess(change.suggested_change_id);
                          setEditableChangeIndex(undefined);
                        })
                        .catch((err) => {
                          console.error(err);
                          props.onActionFailed(
                            "That change did not reach your CRM. It is still pending."
                          );
                        });
                    }}
                    onApprove={(localValue) => {
                      props.onActionInitiated();
                      acceptSuggestedChangesWithModifications(
                        suggestedExplainEntry.cirrom_changes[changeIndex]
                          .suggested_change_id,
                        localValue
                      )
                        .then((_onAcceptResponse) => {
                          displayAlert(
                            "Change successfully accepted.",
                            "success"
                          );
                          onSuccess(
                            suggestedExplainEntry.cirrom_changes[changeIndex]
                              .suggested_change_id
                          );
                          setEditableChangeIndex(undefined);
                        })
                        .catch((err) => {
                          console.error(err);
                          props.onActionFailed(
                            "That change did not reach your CRM. It is still pending."
                          );
                        });
                    }}
                  >
                    <div className={classes.tableCellCRMContents}>
                      {change.cirrom_value &&
                        change.status !== "MultipleChoice" && (
                          <ValueChangeRenderer
                            value={change.cirrom_value}
                            data={{
                              old_value: suggestedExplainEntry?.crm_value || "",
                              new_value: change.cirrom_value,
                              field: suggestedExplainEntry?.field_name || "",
                            }}
                          />
                        )}
                      {change.cirrom_value &&
                        change.status === "MultipleChoice" && (
                          <MultipleChoiceChangeRenderer
                            change={change}
                            onActionInitiated={props.onActionInitiated}
                            onActionFailed={props.onActionFailed}
                            onSuccess={onSuccess}
                            displayAlert={displayAlert}
                          />
                        )}

                      {!change.cirrom_value &&
                        (change.description.Description ||
                          JSON.parse(change.description).Description)}
                    </div>
                  </EditableValueRenderer>

                  <div className={classes.flexBaseline}>
                    <ProbabilityCellRenderer
                      data={{ description: change.description }}
                      value={null}
                      customRenderer={
                        <Box className={classes.ratingBox}>
                          <Rating
                            value={(5 * change.probability) / 100}
                            readOnly
                            size={"small"}
                            precision={0.25}
                          />
                        </Box>
                      }
                    />
                    <ChangeActionRenderer
                      changeIndex={changeIndex}
                      suggestedExplainEntry={suggestedExplainEntry}
                      editableChangeIndex={editableChangeIndex}
                      fieldName={fieldName}
                      onActionInitiated={props.onActionInitiated}
                      onActionFailed={props.onActionFailed}
                      displayAlert={displayAlert}
                      onSuccess={onSuccess}
                    />
                  </div>
                  {suggestedExplainEntry &&
                  changeIndex <
                    suggestedExplainEntry.cirrom_changes.length - 1 ? (
                    <Divider />
                  ) : null}
                </Box>
              </div>
            );
          })}
          {suggestedExplainEntry?.cirrom_changes?.length === 0 && (
            <div className={classes.emptyChangeBox}>
              {[
                "company",
                "vip_contact",
                "contact_manager",
                "address_linkage",
              ].indexOf(suggestedExplainEntry.field_name) < 0 ? (
                <EditableValueRenderer
                  onApprove={(localValue) => {
                    props.onActionInitiated();
                    approveAdHocSuggestedChange(
                      suggestedExplainEntry.field_name,
                      localValue,
                      suggestedExplainEntry.crm_value,
                      { suggestedChangeId: props.suggestedChangeId },
                      suggestedExplainEntry.crm_id
                    )
                      .then((_onAcceptResponse) => {
                        props.displayAlert(
                          "Change successfully accepted.",
                          "success"
                        );
                        props.onSuccess();
                      })
                      .catch((err) => {
                        console.error(err);
                        props.onActionFailed(
                          "That change did not reach your CRM. It is still pending."
                        );
                      });
                  }}
                  value={suggestedExplainEntry.crm_value}
                  suggestedExplainEntry={suggestedExplainEntry}
                >
                  {suggestedExplainEntry.crm_value || <span />}
                </EditableValueRenderer>
              ) : (
                suggestedExplainEntry.crm_value
              )}
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
